import { Camera, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { Avatar } from "@/src/common/components/ui/avatar";
import { clsx } from "@/src/common/utils/clsx";

interface AvatarPickerProps {
	/** Initial avatar URL (remote, persisted). */
	src: string | null | undefined;
	/** Name used to compute the initials fallback. */
	name: string | null | undefined;
	/** Size ring — matches the common Avatar sizes. */
	size?: "lg" | "xl";
	/** Called with the chosen File once the user picks an image.
	 *  The caller is responsible for persistence (upload + DB update). */
	onPick: (file: File) => Promise<void> | void;
	className?: string;
	/** When true, the component shows its own loading state. Parents that own
	 *  the async flow can drive this explicitly. */
	isLoading?: boolean;
	/** Optional preview URL used while the caller hasn't uploaded yet
	 *  (first-login modal shows a local preview before submit). */
	previewSrc?: string | null;
	ariaLabel: string;
}

export function AvatarPicker({ src, name, size = "xl", onPick, className, isLoading, previewSrc, ariaLabel }: AvatarPickerProps) {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [internalLoading, setInternalLoading] = useState(false);
	const loading = isLoading ?? internalLoading;

	const openPicker = () => {
		if (loading) return;
		inputRef.current?.click();
	};

	const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;
		if (isLoading === undefined) setInternalLoading(true);
		try {
			await onPick(file);
		} finally {
			if (isLoading === undefined) setInternalLoading(false);
		}
	};

	return (
		<button
			type="button"
			onClick={openPicker}
			disabled={loading}
			className={clsx(
				"group relative inline-flex shrink-0 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full",
				className,
			)}
			aria-label={ariaLabel}
		>
			<Avatar src={previewSrc ?? src ?? undefined} name={name ?? undefined} size={size} />
			<span
				className={clsx(
					"absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white transition-opacity",
					loading ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
				)}
			>
				{loading ? <Loader2 className="size-5 animate-spin" aria-hidden /> : <Camera className="size-5" aria-hidden />}
			</span>
			<input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleChange} />
		</button>
	);
}
